import { useState, useMemo } from 'react'
import { UserPOI, UserPOIType } from '../../types/userPois'
import { getUserPOITypeById } from '../../data/userPoiTypes'
import { CheckCircle, XCircle, Clock, MapPin, Plus } from 'lucide-react'
import { UserPOIDrawer } from './UserPOIDrawer'
import type { RouteData } from '@cya-routes/shared'
import { calculateMultipleUserPOIDistances, sortUserPOIsByDistance } from '../../services/userPoiDistanceService'

interface UserPOIsPanelProps {
  pois: UserPOI[]
  route?: RouteData
  onPOIClick: (poi: UserPOI) => void
  onStartDrag: (type: UserPOIType) => void
  showResolved: boolean
  onToggleResolved: () => void
  isAuthenticated: boolean
  mobileMode?: boolean // When true, only show Add tab
}

export function UserPOIsPanel({
  pois,
  route,
  onPOIClick,
  onStartDrag,
  showResolved,
  onToggleResolved,
  isAuthenticated,
  mobileMode = false
}: UserPOIsPanelProps) {
  const [activeTab, setActiveTab] = useState<'view' | 'add'>(mobileMode ? 'add' : 'view')
  // Filter POIs based on resolved status
  const visiblePOIs = showResolved ? pois : pois.filter(poi => !poi.resolved)
  
  // Calculate distances and sort POIs
  const sortedPOIs = useMemo(() => {
    if (route && route.coordinates && route.elevation?.profile) {
      // Calculate distances and sort by distance along route
      const poisWithDistance = calculateMultipleUserPOIDistances(visiblePOIs, route)
      return sortUserPOIsByDistance(poisWithDistance)
    } else {
      // Fallback to sorting by creation date (newest first)
      return [...visiblePOIs].sort((a, b) => 
        (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0)
      ).map(userPoi => ({ userPoi, distance: 0, isOffRoute: false, offRouteDistance: 0 }))
    }
  }, [visiblePOIs, route])

  const renderPOI = (poiWithDistance: { userPoi: UserPOI; distance: number; isOffRoute: boolean; offRouteDistance: number }) => {
    const { userPoi: poi, distance, isOffRoute, offRouteDistance } = poiWithDistance
    const poiType = getUserPOITypeById(poi.type)
    if (!poiType) return null

    const Icon = poiType.icon
    const confirmationCount = Object.keys(poi.confirmations || {}).length
    const outdatedCount = Object.keys(poi.outdatedVotes || {}).length
    const totalVotes = confirmationCount + outdatedCount
    const confirmationRate = totalVotes > 0 ? confirmationCount / totalVotes : 0

    return (
      <div
        key={poi.id}
        onClick={() => onPOIClick(poi)}
        className="p-3 bg-gray-700 hover:bg-gray-600 rounded-lg cursor-pointer transition-colors"
      >
        <div className="flex items-start gap-3">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: poi.resolved ? '#6b7280' : poiType.color }}
          >
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-white truncate">
              {poi.name}
            </h4>
            <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
              <span>by {poi.authorName}</span>
              {route && distance > 0 && (
                <>
                  <span>•</span>
                  <span>
                    {isOffRoute 
                      ? `${offRouteDistance.toFixed(1)}km off route`
                      : `${distance.toFixed(1)}km`
                    }
                  </span>
                </>
              )}
            </div>
            
            {/* Status indicators */}
            <div className="flex items-center gap-3 mt-1">
              {poi.resolved && (
                <span className="text-xs text-gray-500">Resolved</span>
              )}
              
              {/* Vote counts */}
              {totalVotes > 0 && (
                <div className="flex items-center gap-3 text-xs">
                  {confirmationCount > 0 && (
                    <span className="text-green-500 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      {confirmationCount}
                    </span>
                  )}
                  {outdatedCount > 0 && (
                    <span className="text-amber-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {outdatedCount}
                    </span>
                  )}
                </div>
              )}
              
              {totalVotes >= 3 && !poi.resolved && (
                <div className="flex items-center gap-2 text-xs">
                  {confirmationRate >= 0.7 && (
                    <span className="text-green-500">
                      Confirmed
                    </span>
                  )}
                  {confirmationRate <= 0.3 && (
                    <span className="text-amber-500">
                      Outdated
                    </span>
                  )}
                </div>
              )}
              {poi.expiresAt && (
                <span className="text-xs text-amber-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Expires {poi.expiresAt.toDate().toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Tabs - Hide in mobile mode */}
      {!mobileMode && (
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('view')}
            className={`
              flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors
              ${activeTab === 'view' 
                ? 'text-emerald-400 border-b-2 border-emerald-400' 
                : 'text-gray-400 hover:text-gray-300'
              }
            `}
          >
            <MapPin className="w-4 h-4" />
            View ({visiblePOIs.length})
          </button>
          <button
            onClick={() => setActiveTab('add')}
            className={`
              flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors
              ${activeTab === 'add' 
                ? 'text-emerald-400 border-b-2 border-emerald-400' 
                : 'text-gray-400 hover:text-gray-300'
              }
            `}
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
      )}

      {activeTab === 'view' ? (
        <>
          {/* Show Resolved Toggle */}
          <div className="p-3 border-b border-gray-700">
            <button
              onClick={onToggleResolved}
              className="w-full flex items-center justify-between px-2 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm text-gray-300 transition-colors"
            >
              <span>Show Resolved</span>
              <div className={`
                w-8 h-5 rounded-full transition-colors relative
                ${showResolved ? 'bg-emerald-600' : 'bg-gray-600'}
              `}>
                <div className={`
                  absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform
                  ${showResolved ? 'translate-x-4' : 'translate-x-0.5'}
                `} />
              </div>
            </button>
          </div>

          {/* POIs List */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {sortedPOIs.length > 0 ? (
              sortedPOIs.map(renderPOI)
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">No reports yet</p>
                <p className="text-xs mt-1">Add one from the "Add" tab</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <UserPOIDrawer 
          onStartDrag={onStartDrag}
          isAuthenticated={isAuthenticated}
          isCompact={false} // Always show full version in sidebar
        />
      )}
    </div>
  )
}